import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ExternalLink, Phone, Video } from "lucide-react";

import ActivityAction from "../../actions/ActivityAction";
import { ActivityTypes } from "../../constants/action-types";
import type { CallRecordingSummary, CrmChatRecordContext } from "../types/chat.types";

export interface CallRecordingPickerProps {
  recordings: CallRecordingSummary[];
  selectedId: string | null;
  onSelect: (activityId: string) => void;
  record?: CrmChatRecordContext | null;
  loading?: boolean;
  disabled?: boolean;
}

function formatStartedAt(iso?: string | null): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function activityLabel(
  recording: CallRecordingSummary,
  t: (key: string) => string
): string {
  const kind = recording.activityType === "call" ? t("Call") : t("Meeting");
  const when = recording.startedAt ? ` · ${formatStartedAt(recording.startedAt)}` : "";
  return `${kind}: ${recording.title}${when}`;
}

const CallRecordingPicker: React.FC<CallRecordingPickerProps> = ({
  recordings,
  selectedId,
  onSelect,
  record,
  loading = false,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUserEmail = useSelector((state: { userReducer?: { getIn: (path: string[]) => unknown } }) => {
    const email = state.userReducer?.getIn(["currentUser", "email"]);
    return typeof email === "string" && email.trim() ? email.trim() : undefined;
  });

  const itemPayload = record?.recordId
    ? {
        item: {
          value: record.recordId,
          label: record.displayName,
          type: record.recordType,
        },
      }
    : null;

  const openActivity = (activityId: string) => {
    if (!record?.recordId) return;
    dispatch(ActivityAction.showActivityDialog(record.recordId, activityId));
  };

  const createActivity = (activityType: string, summary: string) => {
    if (!record?.recordId || !itemPayload) return;
    const attendees = currentUserEmail ? [currentUserEmail] : [];
    dispatch(
      ActivityAction.showActivityDialog(record.recordId, null, {
        ...itemPayload,
        activityType,
        summary,
        description: "",
        organizer: currentUserEmail ?? "",
        attendees,
      })
    );
  };

  if (loading) {
    return (
      <p className="text-xs text-gray-500" data-testid="call-recording-picker-loading">
        {t("Loading calls and meetings…")}
      </p>
    );
  }

  if (recordings.length === 0) {
    return (
      <div
        className="flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50/50 px-3 py-2"
        data-testid="call-recording-picker-empty"
      >
        <p className="text-xs text-amber-900">
          {t(
            "No calls or meetings on this record yet. Log a call or add a meeting, include a transcript, then return here."
          )}
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={disabled || !record?.recordId}
            onClick={() => createActivity(ActivityTypes.call, t("Call") as string)}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Phone className="h-3.5 w-3.5" aria-hidden />
            {t("Log a call")}
          </button>
          <button
            type="button"
            disabled={disabled || !record?.recordId}
            onClick={() => createActivity(ActivityTypes.meeting, t("Meeting") as string)}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Video className="h-3.5 w-3.5" aria-hidden />
            {t("Add meeting")}
          </button>
        </div>
      </div>
    );
  }

  const withTranscript = recordings.filter((r) => r.hasTranscript);
  const withoutTranscript = recordings.filter((r) => !r.hasTranscript);

  if (withTranscript.length === 0) {
    return (
      <div
        className="flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50/50 px-3 py-2"
        data-testid="call-recording-picker-no-transcript"
      >
        <p className="text-xs text-amber-900">
          {t(
            "Calls and meetings exist but none have enough notes for AI yet. Open an activity and add a transcript, or write at least 40 characters in the description or summary, then return here."
          )}
        </p>
        <ul className="flex flex-col gap-1.5">
          {withoutTranscript.map((r) => (
            <li
              key={r.activityId}
              className="flex items-center justify-between gap-2 rounded-md border border-amber-100 bg-white px-2 py-1.5"
            >
              <span className="flex min-w-0 items-center gap-1 text-xs text-gray-800">
                {r.activityType === "call" ? (
                  <Phone className="h-3 w-3 flex-shrink-0 text-gray-500" aria-hidden />
                ) : (
                  <Video className="h-3 w-3 flex-shrink-0 text-gray-500" aria-hidden />
                )}
                <span className="truncate">{activityLabel(r, t)}</span>
              </span>
              <button
                type="button"
                disabled={disabled || !record?.recordId}
                onClick={() => openActivity(r.activityId)}
                className="inline-flex flex-shrink-0 items-center gap-0.5 rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-700 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t("Open activity")}
                <ExternalLink className="h-3 w-3" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1" data-testid="call-recording-picker">
      <label htmlFor="call-recording-select" className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
        {t("Call / meeting")}
      </label>
      <select
        id="call-recording-select"
        value={selectedId ?? ""}
        onChange={(e) => onSelect(e.target.value)}
        disabled={disabled}
        className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-200 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <option value="" disabled>
          {t("Select a call or meeting")}
        </option>
        {withTranscript.map((r) => (
          <option key={r.activityId} value={r.activityId}>
            {activityLabel(r, t)}
          </option>
        ))}
      </select>
      {withoutTranscript.length > 0 && record?.recordId ? (
        <details className="text-[11px] text-gray-600">
          <summary className="cursor-pointer hover:text-gray-800">
            {t("Other calls and meetings without enough notes")}
          </summary>
          <ul className="mt-1 flex flex-col gap-1">
            {withoutTranscript.map((r) => (
              <li key={r.activityId} className="flex items-center justify-between gap-2">
                <span className="truncate">{activityLabel(r, t)}</span>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => openActivity(r.activityId)}
                  className="flex-shrink-0 text-blue-600 hover:underline disabled:opacity-50"
                >
                  {t("Open")}
                </button>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {selectedId ? (
        <p className="flex items-center gap-1 text-[11px] text-gray-500">
          {recordings.find((r) => r.activityId === selectedId)?.activityType === "call" ? (
            <Phone className="h-3 w-3" aria-hidden />
          ) : (
            <Video className="h-3 w-3" aria-hidden />
          )}
          {t("Call/meeting notes or transcript will be included in the AI request")}
        </p>
      ) : null}
    </div>
  );
};

export default CallRecordingPicker;
